import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import FilterOptionList from "@/components/Filters/FilterDropdown/components/FilterOptionList";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import { AutocompleteProps as MuiAutocompleteProps } from "@mui/material/Autocomplete";

import {
  checkIfTranslationExists,
  Filter,
  ROUTES,
  TRANSLATABLE_OBJECTS
} from "../../../shared";
import RectangleLoader from "../../../shared/components/loaders/RectangleLoader";
import Autocomplete from "../../Autocomplete/Autocomplete";
import useFilterDropdown from "../hooks/useFilterDropdown";
import { useStyles } from "../styles";
import { FILTER_NAMES } from "../types";

export interface FilterDropdownProps {
  route: ROUTES;
  filterName: FILTER_NAMES;
  placeholder: string;
  translateString?: string;
}

const FilterDropdown = ({
  route,
  filterName,
  placeholder,
  translateString,
  ...props
}: FilterDropdownProps &
  Omit<
    MuiAutocompleteProps<any, boolean, boolean, boolean>,
    "renderInput" | "size" | "options"
  >) => {
  const { i18n } = useTranslation();

  const {
    loading: isTableLoading,
    initialLoading,
    params
  } = useTableStoreByRoute(route);

  const filterObject = params.filters;

  const {
    isOpen,
    isLoading,
    options,
    setIsOpen,
    getFilterOptions,
    handleFilterChangeValue
  } = useFilterDropdown(route, filterName);

  const [value, setValue] = useState<Filter[]>([]);
  const [selectedItem, setSelectedItem] = useState<Filter | null>(null);

  const { filterDropdownCustomWidth } = useStyles({});

  // Clear value when filters are reset
  useEffect(() => {
    if (!filterObject) {
      setValue([]);
    }
  }, [filterObject]);

  // Sync value with store's filter object
  useEffect(() => {
    const filterValue = filterObject?.[filterName];
    if (filterValue !== undefined) {
      setValue(filterValue);
    } else if (!filterObject) {
      setValue([]);
    }
  }, [filterObject, filterName]);

  const handleChange = (event: any, newValue: Filter[]) => {
    event.preventDefault();

    handleFilterChangeValue({
      isTableLoading,
      filterObject,
      newValue,
      filterName,
      setSelectedItem,
      setValue
    });
  };

  const getOptionLabel = (option: Filter) => {
    const name = String(option.name);

    if (translateString) {
      return checkIfTranslationExists(
        name,
        translateString as TRANSLATABLE_OBJECTS,
        i18n
      );
    }

    return name;
  };

  const handleOpen = () => {
    setIsOpen(true);
    getFilterOptions();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return initialLoading ? (
    <RectangleLoader
      customStyle={filterDropdownCustomWidth}
      height={36}
      testId={`${filterName}-filter-loader`}
    />
  ) : (
    <Autocomplete
      {...props}
      data-testid={`${filterName}-filter-dropdown`}
      fullWidth
      multiple
      size="small"
      includeInputInList
      disableClearable
      disableCloseOnSelect
      open={isOpen}
      value={value}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      loading={isLoading}
      onChange={(e, newFilterValue) => handleChange(e, newFilterValue)}
      renderOption={(props, option, { selected }) => (
        <FilterOptionList
          key={option.id}
          optionName={getOptionLabel(option)}
          props={props}
          option={option}
          selected={selected}
          isTableLoading={isTableLoading}
          selectedItem={selectedItem}
        />
      )}
      renderTags={() => null}
      textFieldParams={{
        placeholder
      }}
      getOptionLabel={(option: Filter) => getOptionLabel(option)}
    />
  );
};

export default FilterDropdown;
